import { TodaySection } from "./today-section";
import { EmptyState } from "@/components/v2/empty-state";
import { useCommissionSummary } from "@/hooks/use-api";
import { formatCompactCurrency } from "@/lib/utils";

// ADV-799: Commission Snapshot section. Renders the three per-status amounts
// (Pending / Payable / Paid) plus the pay-period window. Density mirrors the
// mockup's compact three-up grid (apps/web/public/mockups/mockup--today.css:155-196)
// using shadcn + an inline <dl>.

interface CommissionSummary {
  pay_period_start: string | null;
  pay_period_end: string | null;
  pay_date: string | null;
  pending_amount: number;
  payable_amount: number;
  paid_amount: number;
  total_amount: number;
  count: number;
}
interface SummaryResponse {
  summary?: CommissionSummary;
}

function formatPeriod(summary: CommissionSummary): string {
  if (!summary.pay_period_start || !summary.pay_period_end) {
    return "No active pay period";
  }
  const start = new Date(summary.pay_period_start);
  const end = new Date(summary.pay_period_end);
  const fmt = (d: Date) =>
    d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${fmt(start)} – ${fmt(end)}`;
}

interface SectionProps {
  title?: string;
}

export function CommissionSnapshotSection({
  title = "Commission snapshot",
}: SectionProps = {}) {
  const { data, isLoading, isError } = useCommissionSummary();
  const summary = (data as SummaryResponse | undefined)?.summary;

  return (
    <TodaySection id="today-section-commission" title={title}>
      {isLoading ? (
        <div className="h-20 animate-pulse rounded-md bg-[color:var(--color-surface-2)]" />
      ) : isError ? (
        <EmptyState
          title="Couldn't load commission snapshot"
          description="Try refreshing the page."
        />
      ) : !summary || summary.count === 0 ? (
        <EmptyState
          title="No commission activity yet"
          description="Snapshots populate after the first commission lands in the current pay period."
        />
      ) : (
        <div className="space-y-3">
          <div className="text-xs uppercase tracking-wide text-[color:var(--color-text-md)]">
            {formatPeriod(summary)}
          </div>
          <dl className="grid grid-cols-3 gap-4">
            <div>
              <dt className="text-xs text-[color:var(--color-text-md)]">
                Pending
              </dt>
              <dd className="mt-0.5 typo-heading tabular-nums text-[color:var(--color-text-hi)]">
                {formatCompactCurrency(summary.pending_amount)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-[color:var(--color-text-md)]">
                Payable
              </dt>
              <dd className="mt-0.5 typo-heading tabular-nums text-[color:var(--color-text-hi)]">
                {formatCompactCurrency(summary.payable_amount)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-[color:var(--color-text-md)]">
                Paid
              </dt>
              <dd className="mt-0.5 typo-heading tabular-nums text-[color:var(--color-text-hi)]">
                {formatCompactCurrency(summary.paid_amount)}
              </dd>
            </div>
          </dl>
        </div>
      )}
    </TodaySection>
  );
}
